import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CopyButton from "@components/buttons/CopyButton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import DataPill from "@components/pills/DataPill";
import GlobalSearchInputField from "@components/inputs/GlobalSearchInputField";
import { kycV2Service } from "@services/apis/KycV2";
import {
  getKycV2OverallStatus,
  KycV2ListRecord,
} from "@services/models/Kyc/KycV2Models";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SettledAccountReponseSlider from "../../modules/settleaccounts/components/SettledAccountReponseSlider";
import ConstantFunctions from "../../utils/constants_functions";
import KycV2ApprovalDetails from "./KycV2ApprovalDetails";

const OVERALL_STATUS_FILTERS = [
  "incomplete",
  "pending_review",
  "approved",
  "rejected",
  "retry",
];

const StatusBadge: React.FC<{ status?: string | null }> = ({ status }) => {
  return <DataPill value={status} />;
};

const KycV2ApprovalList: React.FC = () => {
  const constantFunctions = new ConstantFunctions();
  const gridRef = useRef<AgGridReact>(null);
  const loadingRef = useRef<boolean>(false);

  const [kycData, setKycData] = useState<KycV2ListRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const rowsPerPage = 1000;

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState<string>("");
  const [filterOverallStatus, setFilterOverallStatus] = useState<
    string | undefined
  >();

  const [showDumpModal, setShowDumpModal] = useState<boolean>(false);
  const [modalData, setModalData] = useState<any>(null);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState<boolean>(false);

  const handleShowDump = useCallback((dumpData: any, title: string) => {
    setModalData(dumpData);
    setModalTitle(title);
    setShowDumpModal(true);
  }, []);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Updated At",
        field: "updatedAt",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 140,
        valueFormatter: (params) =>
          constantFunctions.dateFormatInDDMMYY(params.value),
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
      },
      {
        headerName: "Name",
        field: "fullName",
        flex: 1.2,
        minWidth: 130,
        maxWidth: 200,
        valueGetter: (params) =>
          `${params.data?.firstName ?? ""} ${params.data?.lastName ?? ""}`.trim() ||
          "-",
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          lineHeight: "1.2",
        },
      },
      {
        headerName: "User ID",
        field: "userId",
        flex: 0.7,
        minWidth: 80,
        maxWidth: 120,
        cellRenderer: (params: any) => {
          const userId = params.data?.userId;
          if (userId == null) {
            return (
              <span className="text-[10px] text-text-base font-medium">-</span>
            );
          }
          return (
            <div className="flex items-center justify-between h-full gap-1">
              <span className="text-[10px] text-text-base font-medium truncate">
                {userId}
              </span>
              <CopyButton
                copyText={userId.toString()}
                copyMessage="User ID copied!"
                copyIconSize={10}
                buttonSize="14px"
                borderRadius="2px"
              />
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Email",
        field: "email",
        flex: 2,
        minWidth: 200,
        maxWidth: 300,
        cellRenderer: (params: any) => {
          const email = params.data?.email;
          if (!email) {
            return (
              <span className="text-[10px] text-text-base font-medium">-</span>
            );
          }
          return (
            <div className="flex items-center justify-between h-full gap-1">
              <span className="text-[10px] text-text-base font-medium truncate flex-1">
                {email}
              </span>
              <CopyButton
                copyText={email}
                copyMessage="Email copied!"
                copyIconSize={10}
                buttonSize="14px"
                borderRadius="2px"
              />
            </div>
          );
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Basic",
        field: "basicStatus",
        flex: 0.9,
        minWidth: 90,
        maxWidth: 130,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: ({ data }: any) => (
          <StatusBadge status={data?.basicStatus} />
        ),
      },
      {
        headerName: "POI",
        field: "poiStatus",
        flex: 0.9,
        minWidth: 90,
        maxWidth: 130,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: ({ data }: any) => (
          <StatusBadge status={data?.poiStatus} />
        ),
      },
      {
        headerName: "POA",
        field: "poaStatus",
        flex: 0.9,
        minWidth: 90,
        maxWidth: 130,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: ({ data }: any) => (
          <StatusBadge status={data?.poaStatus} />
        ),
      },
      {
        headerName: "AML",
        field: "amlStatus",
        flex: 0.9,
        minWidth: 90,
        maxWidth: 130,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: ({ data }: any) => (
          <StatusBadge status={data?.amlStatus} />
        ),
      },
      {
        headerName: "Liveness",
        field: "livenessStatus",
        flex: 0.9,
        minWidth: 90,
        maxWidth: 130,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: ({ data }: any) => (
          <StatusBadge status={data?.livenessStatus} />
        ),
      },
      {
        headerName: "Overall",
        field: "overallStatus",
        flex: 1.1,
        minWidth: 110,
        maxWidth: 160,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        valueGetter: (params) => getKycV2OverallStatus(params.data),
        cellRenderer: ({ data }: any) => (
          <StatusBadge status={getKycV2OverallStatus(data)} />
        ),
      },
      {
        headerName: "POI Dump",
        field: "poiDump",
        flex: 1,
        minWidth: 110,
        maxWidth: 150,
        cellRenderer: (props: any) => (
          <LightCustomButton
            text="View POI"
            isActive={true}
            onClick={() =>
              handleShowDump(props.data?.poiDump, "POI Verification Data")
            }
            textSize="10px"
            className="!h-[24px] !flex-none w-full flex items-center justify-center"
          />
        ),
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: false,
        sortable: false,
      },
      {
        headerName: "POA Dump",
        field: "poaDump",
        flex: 1,
        minWidth: 110,
        maxWidth: 150,
        cellRenderer: (props: any) => (
          <LightCustomButton
            text="View POA"
            isActive={true}
            onClick={() =>
              handleShowDump(props.data?.poaDump, "POA Verification Data")
            }
            textSize="10px"
            className="!h-[24px] !flex-none w-full flex items-center justify-center"
          />
        ),
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: false,
        sortable: false,
      },
      {
        headerName: "AML Dump",
        field: "amlDump",
        flex: 1,
        minWidth: 110,
        maxWidth: 150,
        cellRenderer: (props: any) => (
          <LightCustomButton
            text="View AML"
            isActive={true}
            onClick={() =>
              handleShowDump(props.data?.amlDump, "AML Verification Data")
            }
            textSize="10px"
            className="!h-[24px] !flex-none w-full flex items-center justify-center"
          />
        ),
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: false,
        sortable: false,
      },
    ],
    [constantFunctions, handleShowDump],
  );

  const fetchKycV2Records = useCallback(
    async (pageNumber = 1, append = false, search?: string) => {
      if (loadingRef.current) return;

      try {
        loadingRef.current = true;
        setLoading(true);
        setError("");

        const trimmedSearch = search?.trim();
        const isEmailSearch = Boolean(
          trimmedSearch && !/^\d+$/.test(trimmedSearch),
        );

        const response = await kycV2Service.fetchRecords({
          page: pageNumber,
          limit: rowsPerPage,
          userId: trimmedSearch && !isEmailSearch ? trimmedSearch : undefined,
          email: isEmailSearch ? trimmedSearch : undefined,
          overallKycStatus: filterOverallStatus?.toLowerCase(),
        });

        const records = Array.isArray(response.data?.records)
          ? response.data.records
          : [];

        if (append) {
          setKycData((prev) => [...prev, ...records]);
        } else {
          setKycData(records);
        }

        setHasMore(records.length === rowsPerPage);
      } catch (err: any) {
        setError("Failed to fetch KYC V2 records");
        console.error(err);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [filterOverallStatus],
  );

  const resetListState = useCallback(() => {
    setKycData([]);
    setPage(1);
    setHasMore(true);
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = useCallback(() => {
    const normalizedSearchTerm = searchTerm.trim();

    if (normalizedSearchTerm === appliedSearchTerm) {
      resetListState();
      fetchKycV2Records(1, false, normalizedSearchTerm || undefined);
      return;
    }

    setAppliedSearchTerm(normalizedSearchTerm);
  }, [appliedSearchTerm, fetchKycV2Records, resetListState, searchTerm]);

  const handleClearSearch = useCallback(() => {
    if (!searchTerm && !appliedSearchTerm) {
      return;
    }

    setSearchTerm("");

    if (appliedSearchTerm === "") {
      resetListState();
      fetchKycV2Records(1, false);
      return;
    }

    setAppliedSearchTerm("");
  }, [appliedSearchTerm, fetchKycV2Records, resetListState, searchTerm]);

  const handleOverallStatusChange = useCallback((status: string) => {
    setFilterOverallStatus((prev) => (prev === status ? undefined : status));
    setSearchTerm("");
    setAppliedSearchTerm("");
  }, []);

  const handleRefresh = useCallback(() => {
    resetListState();
    fetchKycV2Records(1, false, appliedSearchTerm || undefined);
  }, [appliedSearchTerm, fetchKycV2Records, resetListState]);

  const loadMoreData = useCallback(() => {
    if (!loadingRef.current && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchKycV2Records(nextPage, true, appliedSearchTerm || undefined);
    }
  }, [appliedSearchTerm, fetchKycV2Records, hasMore, page]);

  const onBodyScroll = useCallback(
    (params: any) => {
      if (!hasMore || loading) return;

      const api = params.api;
      const displayedRowCount = api.getDisplayedRowCount();
      const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

      if (
        lastDisplayedRowIndex >= displayedRowCount - 5 &&
        displayedRowCount > 0
      ) {
        loadMoreData();
      }
    },
    [hasMore, loadMoreData, loading],
  );

  useEffect(() => {
    resetListState();
    fetchKycV2Records(1, false, appliedSearchTerm || undefined);
  }, [
    appliedSearchTerm,
    fetchKycV2Records,
    filterOverallStatus,
    resetListState,
  ]);

  const handleCloseDumpModal = () => {
    setShowDumpModal(false);
  };

  const onRowClicked = (event: RowClickedEvent) => {
    const selectedData = event.data as KycV2ListRecord;

    const allRows = document.querySelectorAll(".ag-row");
    allRows.forEach((row) => {
      row.classList.remove("ag-row-selected-custom");
    });

    if (event.rowIndex !== null) {
      const rowElement = document.querySelector(
        `[row-index="${event.rowIndex}"]`,
      );
      if (rowElement) {
        rowElement.classList.add("ag-row-selected-custom");
      }
    }

    if (selectedData?.userId != null) {
      setSelectedUserId(Number(selectedData.userId));
      setIsPanelCollapsed(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-row items-stretch">
        <div
          className={`flex min-h-0 flex-1 flex-col overflow-hidden bg-bg-page-body py-[20px] pr-[12px] ${
            selectedUserId && !isPanelCollapsed ? "w-3/4" : "w-full"
          }`}
        >
          <div className="mb-4 flex shrink-0 flex-row items-center justify-between">
            <div className="flex justify-start space-x-3 items-center">
              <p className="text-text-base text-[10px] font-bold">
                Filter by Overall Status:
              </p>
              <div className="flex space-x-3">
                {OVERALL_STATUS_FILTERS.map((status) => (
                  <div
                    key={status}
                    onClick={
                      loading
                        ? undefined
                        : () => handleOverallStatusChange(status)
                    }
                    className={`border py-[4px] px-[16px] text-text-base text-[10px] font-medium rounded-[100px] ${
                      loading
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    } ${
                      filterOverallStatus === status
                        ? "border-border-button-secondary bg-bg-button-secondary"
                        : "border-border-subtle bg-bg-page-body"
                    }`}
                  >
                    {status
                      .replace("_", " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </div>
                ))}
              </div>
            </div>
            <div className="w-[360px] flex-shrink-0">
              <GlobalSearchInputField
                value={searchTerm}
                onChange={handleSearchChange}
                onSearch={handleSearch}
                onClear={handleClearSearch}
                disabled={loading}
                isSearchApplied={Boolean(appliedSearchTerm)}
                placeholder="Search by User ID / Email"
              />
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            {loading && kycData.length === 0 ? (
              <AgGridSkeleton
                columnDefs={columnDefs}
                style={{ height: "100%", width: "100%", minHeight: 0 }}
              />
            ) : (
              <AgGridReactTable
                ref={gridRef}
                rowData={kycData}
                columnDefs={columnDefs}
                onRowClicked={onRowClicked}
                onBodyScroll={onBodyScroll}
                getRowId={(params) =>
                  params.data?.userId != null
                    ? params.data.userId.toString()
                    : String(params.data?.id ?? params.node?.id ?? "")
                }
                error={error}
                style={{ height: "100%", width: "100%", minHeight: 0 }}
              />
            )}
          </div>
        </div>

        {selectedUserId != null && (
          <div
            className={`min-h-0 flex-shrink-0 self-stretch bg-bg-page-body py-4 border-l border-border-subtle transition-all duration-300 relative ${
              isPanelCollapsed ? "w-0 px-0" : "w-1/4 px-2"
            }`}
          >
            <button
              onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
              className="absolute top-1/2 -translate-y-1/2 z-20 bg-bg-button-secondary text-text-base rounded-lg shadow-lg transition-all duration-200 flex items-center justify-center group"
              style={{
                left: isPanelCollapsed ? "calc(100% - 32px)" : "-16px",
                width: "32px",
                height: "60px",
              }}
              aria-label={isPanelCollapsed ? "Expand panel" : "Collapse panel"}
            >
              <span className="material-symbols-outlined text-[20px] group-hover:scale-110 transition-transform">
                {isPanelCollapsed ? "chevron_left" : "chevron_right"}
              </span>
            </button>

            <div
              className={`overflow-auto h-full transition-opacity duration-300 ${
                isPanelCollapsed
                  ? "opacity-0 pointer-events-none"
                  : "opacity-100"
              }`}
            >
              <KycV2ApprovalDetails
                userId={selectedUserId}
                onRefresh={handleRefresh}
              />
            </div>
          </div>
        )}
      </div>

      <SettledAccountReponseSlider
        isOpen={showDumpModal}
        onClose={handleCloseDumpModal}
        data={modalData}
        isSuccess={true}
        title={modalTitle}
      />
    </div>
  );
};

export default KycV2ApprovalList;
